
  import { Component, Vue } from 'vue-property-decorator';
  import { mapGetters } from 'vuex';

  import { ICookieModel } from '@/models/cookie.model';
  import { IUserModel } from '@/models/account.model';

  import accountService from '@/services/account.service';
  import cookieService from '@/services/cookie.service';

  @Component({
    computed: {
      ...mapGetters([
        'user',
        'isLoading',
        'isLinkingCallbackLoading'
      ])
    }
  })
  export default class CookiesControl extends Vue {
    public data(): object {
      return {
        cookiesList: [] as Array<ICookieModel>,
        hasCustomCookiesManagement: false,
        showCookiesButton: false,
        showCookiesControlModal: false,
        showCookiesSettings: false,
        unwatch: null,
        cookiesPolicy: "",
      }
    }

    public created(): void {
      this.$data.showCookiesSettings = this.$data.RCEnvironment.VUE_APP_HAS_CUSTOM_COOKIES_MANAGEMENT === false;
      this.$data.showCookiesButton = this.$data.RCEnvironment.VUE_APP_HAS_CUSTOM_COOKIES_MANAGEMENT === true;
      this.$data.hasCustomCookiesManagement = this.$data.RCEnvironment.VUE_APP_HAS_CUSTOM_COOKIES_MANAGEMENT == true;

      this.initPrivacyLinks();

      if (this.$data.RCEnvironment.VUE_APP_HAS_CUSTOM_COOKIES_MANAGEMENT === false) {
        this.setCookiesSettings();

        this.$data.unwatch = this.$store.watch(() => this.$store.getters.user, user => {
          this.loadCookies();
          this.setCookiesSettings();
        })

        this.loadCookies();
      }
    }

    public mounted(): void {
      this.$watch(() => this.$store.getters.privacyLinks, (params) => {
        this.initPrivacyLinks();
      });
    }

    public destroyed(): void {
      this.$data.unwatch();
    }

    public goToCookiesSettings(): void {
      if (this.$store.getters.user && this.$store.getters.user.Id) {
        this.$router.push({ name: "Cookies" });
      }
      else {
        this.$set(this.$data, "showCookiesControlModal", true);
      }
    }

    public acceptAllCookies(): void {
      const user = this.$store.getters.user;
      if (user && user.Id) {

        const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "cookie/user/" + user.Id + "/accept-all";
        accountService.post(this, url, { IsAllReject: false }, { headers: { Authorization: "Bearer " + this.$store.getters.token.Value } }).then(() => {
          this.setAllCookies();
        });
      }
      else {
        this.setAllCookies();
        this.$set(this.$data, "showCookiesButton", true);
      }
    }

    public rejectAllCookies(): void {
      const user = this.$store.getters.user;

      if (user && user.Id) {
        const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "cookie/user/" + user.Id + "/accept-all";
        accountService.post(this, url, { IsAllReject: true }, { headers: { Authorization: "Bearer " + this.$store.getters.token.Value } });
      }
      else {
        this.$data.cookiesList.forEach(((cookie: ICookieModel) => cookie.IsAccepted = false));
        this.$set(this.$data, "showCookiesButton", true);
      }

      cookieService.setConsentCookies(this);
      this.$set(this.$data, "showCookiesSettings", false);
      this.$set(this.$data, "showCookiesControlModal", false);
    }

    public setAllCookies(): void {
      cookieService.setConsentCookies(this);

      cookieService.enableAnalytics(this);
      cookieService.enableAnalytics(this, true);
      cookieService.triggerHistoryEvent(this);

      this.$set(this.$data, "showCookiesSettings", false);
      this.$data.cookiesList.forEach(((cookie: ICookieModel) => cookie.IsAccepted = true));
    }

    public setCookie(cookie: ICookieModel): void {
      if (!cookie.IsActive) {
        return;
      }

      cookie.IsAccepted = !cookie.IsAccepted;
    }

    public confirmCookies(): void {
      this.$data.cookiesList.forEach((cookie: ICookieModel) => {
        if (cookie.Name === "Analytical cookies") {
          cookie.IsAccepted ? cookieService.enableAnalytics(this) : cookieService.disableAnalytics(this);
        }
        else if (cookie.Name === "Marketing cookies") {
          cookie.IsAccepted ? cookieService.enableAnalytics(this, true) : cookieService.disableAnalytics(this, true);
        }

        if (cookie.IsAccepted) {
          cookieService.triggerHistoryEvent(this);
        }
      });

      cookieService.setConsentCookies(this);
      this.$set(this.$data, "showCookiesSettings", false);
      this.$set(this.$data, "showCookiesControlModal", false);
    }

    public openCookiesSettings(): void {
      this.$set(this.$data, "showCookiesControlModal", !this.$data.showCookiesControlModal);
      this.$set(this.$data, "showCookiesSettings", this.$data.showCookiesControlModal);
      this.$set(this.$data, "showCookiesButton", false);
    }

    private initPrivacyLinks(): void {
      this.$data.cookiesPolicy = this.$store.getters.privacyLinks.CookiePolicy;
    }

    private setCookiesSettings(): void {
      const consentCookie: string = this.$cookies.get("consent_cookies");

      let showCookiesSettings: boolean = consentCookie !== null && consentCookie === "1" ? false : true;

      const user: IUserModel = this.$store.getters.user;
      if (user && user.Id) {
        if (!showCookiesSettings && !user.IsCookiePopupSeen) {
          cookieService.seeCookiePopup(this)
            .then(() => {
              cookieService.setConsentCookies(this);
              this.$set(this.$data, "showCookiesButton", false);
              this.$set(this.$data, "showCookiesSettings", false);
            });
        } else {
          showCookiesSettings = !user.IsCookiePopupSeen;

          if (user.IsCookiePopupSeen) {
            cookieService.setConsentCookies(this);
          }

          this.$set(this.$data, "showCookiesButton", false);
          this.$set(this.$data, "showCookiesSettings", showCookiesSettings);
        }
      } else {
        this.$set(this.$data, "showCookiesButton", !showCookiesSettings);
        this.$set(this.$data, "showCookiesSettings", showCookiesSettings);
      }
    }

    private loadCookies(): void  {
      cookieService.getCookiesList(this)
        .then((cookies: Array<ICookieModel>) => {
          const consentAnalytics: string = this.$cookies.get("consent_analytics");
          const isConsentAnalyticsAccepted: boolean = consentAnalytics !== null && consentAnalytics === "1";
          const analyticalCookie: ICookieModel | undefined = cookies.find(cookie => cookie.Name === "Analytical cookies");
          if (isConsentAnalyticsAccepted && analyticalCookie) {
            analyticalCookie.IsAccepted = true;
          }

          const consentMarketing: string = this.$cookies.get("consent_marketing");
          const isConsentMarketingAccepted: boolean = consentMarketing !== null && consentMarketing === "1";
          const marketingCookie: ICookieModel | undefined = cookies.find(cookie => cookie.Name === "Marketing cookies");
          if (isConsentMarketingAccepted && marketingCookie) {
            marketingCookie.IsAccepted = true;
          }

          this.$set(this.$data, "cookiesList", cookies);
          this.$store.dispatch("updateCookies", cookies);
        })
        .catch(error => console.log("error", error));
    }
  }
